import { normalizeLocationText } from "./locationSearch";
import type { CitySelection, StreetReference, StreetSelection } from "./types";

export const normalizeStreetText = (value: string) =>
  normalizeLocationText(value)
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const searchStreets = (streets: StreetReference[], query: string, limit = 7) => {
  const normalized = normalizeStreetText(query);
  if (!normalized) return [];

  return streets
    .flatMap((street) => {
      const searchable = normalizeStreetText(street.nameNormalized || street.name);
      const words = searchable.split(" ");
      const rank = searchable === normalized
        ? 0
        : searchable.startsWith(normalized)
          ? 1
          : words.some((word) => word.startsWith(normalized))
            ? 2
            : searchable.includes(normalized)
              ? 3
              : undefined;
      return rank === undefined ? [] : [{ street, rank }];
    })
    .sort((first, second) => first.rank - second.rank || first.street.name.localeCompare(second.street.name, "sk"))
    .slice(0, Math.max(1, limit))
    .map(({ street }) => street);
};

export const datasetStreetSelection = (street: StreetReference): StreetSelection => ({
  street: street.name,
  streetNormalized: street.nameNormalized || normalizeStreetText(street.name),
  streetSource: "dataset",
  streetId: street.id
});

export const customStreetSelection = (street: string): StreetSelection => ({
  street,
  streetNormalized: normalizeLocationText(street),
  streetSource: "custom",
  streetId: undefined
});

type PropertyLocation = {
  city: string;
  cityNormalized?: string;
  citySource?: "dataset" | "custom";
  municipalityId?: string;
  street: string;
  streetNormalized?: string;
  streetSource?: "dataset" | "custom";
  streetId?: string;
};

export const locationAfterCityChange = <T extends PropertyLocation>(
  property: T,
  city: CitySelection
): T => {
  const municipalityChanged = property.municipalityId !== city.municipalityId
    || property.cityNormalized !== city.cityNormalized;

  return {
    ...property,
    ...city,
    ...(municipalityChanged
      ? property.street
        ? customStreetSelection(property.street)
        : { streetNormalized: "", streetSource: undefined, streetId: undefined }
      : {})
  } as T;
};
